import React, { useEffect, useState } from "react";
import { useGlobalModalContext } from "@/providers/modal-provider/modal-provider";
import { signIn } from "next-auth/react";
import { changeFileType } from "@/utils/files";
import { useMobile } from "@/hooks/use-responsive";
import { isYouTubeUrl } from "@/services/youtube-downloader/yt-api/utils";
import { useYoutubeTracking } from "@/hooks/mixpanel/youtube/use-youtube-tracking";

export type YouTubeAudio = {
  file?: File;
  title: string;
  icon: string;
};

type ContextChanger = {
  changeContextCallback: (audio: YouTubeAudio) => void;
  deleteInContextCallback: () => void;
};

export const useYoutubeUrlDownload = (contextChanger: ContextChanger) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLinkValid, setIsLinkValid] = useState(false);
  const [songLinkValue, setSongLinkValue] = useState("");
  const [youtubeAudio, setYoutubeAudio] = useState<YouTubeAudio | null>(null);
  const [musicPlayerFileUrl, setMusicPlayerFileUrl] = useState<string>("");
  const { isMobile } = useMobile();
  const { showModal } = useGlobalModalContext();
  const { trackDownloadSuccess, trackDownloadError } = useYoutubeTracking();

  const showErrorModal = (errorCode: string) => {
    let title = "YouTube Error";
    let message = "Something went wrong.";
    switch (errorCode) {
      case "too_long":
        message = "YouTube video too long. It might be less than 10 min.";
        break;
      case "invalid_url":
        message = "Invalid YouTube link. Please double check the link.";
        break;
      case "no_service_is_available":
        message =
          "At the moment, downloading from YouTube is not available. Please try again later.";
        break;
    }

    showModal({
      type: "error_modal",
      props: {
        title: title,
        message: message,
      },
    });
  };

  useEffect(() => {
    if (youtubeAudio?.file) {
      if (isMobile) {
        changeFileType(youtubeAudio.file, "audio/mpeg")
          .then((newFile) => {
            const newUrl = URL.createObjectURL(newFile);
            setMusicPlayerFileUrl(newUrl);

            return () => {
              URL.revokeObjectURL(newUrl);
            };
          })
          .catch((error) => {
            console.error("Error changing file type:", error);
          });
      }
      const newUrl = URL.createObjectURL(youtubeAudio.file);
      setMusicPlayerFileUrl(newUrl);

      return () => {
        URL.revokeObjectURL(newUrl);
      };
    }
  }, [isMobile, youtubeAudio]);

  const handleLinkInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const linkValue = event.target.value;
    setSongLinkValue(linkValue);
    setIsLinkValid(isYouTubeUrl(linkValue));
  };

  const handleDeleteYoutubeFile = () => {
    contextChanger.deleteInContextCallback();
  };

  const handleDownload = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        "/api/yt?" + new URLSearchParams({ url: songLinkValue }),
      );
      if (response.status === 401) {
        setIsLoading(false);
        signIn();
        return;
      }

      if (response.status === 400) {
        setIsLoading(false);
        const json = await response.json();
        trackDownloadError(json.error, songLinkValue);
        showErrorModal(json.error);
        return;
      }

      const headerFileName = response.headers.get("X-File-Name");
      const fileName = decodeURI(headerFileName ?? "");

      trackDownloadSuccess(fileName);
      const blob = await response.blob();
      const ytFile = new File([blob], fileName);
      setMusicPlayerFileUrl(URL.createObjectURL(ytFile));
      const icon = response.headers.get("X-File-Icon") ?? "";

      const youtubeAudio = {
        title: fileName,
        icon: icon,
        file: ytFile,
      };
      setYoutubeAudio(youtubeAudio);
      contextChanger.changeContextCallback(youtubeAudio);
    } catch (error) {
      console.error("Error downloading YouTube video:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isLinkValid,
    songLinkValue,
    youtubeAudio,
    musicPlayerFileUrl,
    setYoutubeAudio,
    handleDownload,
    handleDeleteYoutubeFile,
    handleLinkInputChange,
  };
};
